/*
 * pdp-sample-toggle.js
 * <pdp-sample-toggle> wraps the standard PDP buy box when the "Sample / Wallpaper toggle"
 * section setting is on (see snippets/custom-new-product__form.liquid).
 *
 * The product's real Type radios stay hidden in the theme's options list, so the theme's own
 * variant UI still picks the variant (Colour + Type + Scale) and Add to Cart submits it. The
 * visible toggle at the top of the buy box is a pair of proxy radios: choosing one checks the
 * matching hidden Type radio and fires its change event. This component also handles:
 *   - data-mode="sample|wallpaper" (CSS shows the matching panel)
 *   - the sq ft stepper and live order total (replaces the old inline buy-box script)
 *   - the Add to Cart label ("Add sample to cart" / "Add [qty] sq ft of wallpaper")
 *   - the low-quantity "switch to a sample" nudge
 *   - quantity forced to 1 in Sample mode
 */
if (!customElements.get('pdp-sample-toggle')) {
  customElements.define(
    'pdp-sample-toggle',
    class PdpSampleToggle extends HTMLElement {
      connectedCallback() {
        this.typeInputName = this.dataset.typeInputName;
        this.threshold = parseInt(this.dataset.threshold, 10) || 0;
        this.sampleLabel = this.dataset.sampleLabel || '';
        this.wallpaperLabel = this.dataset.wallpaperLabel || '';
        this.prices = this.readPrices();

        this.variantSelection = this.querySelector('variant-selection');
        this.quantityInput = this.querySelector('.quantity-input');
        this.minusButton = this.querySelector('.quantity-minus');
        this.plusButton = this.querySelector('.quantity-plus');
        this.totalEl = this.querySelector('.price-value');
        this.warningEl = this.querySelector('[data-low-qty-warning]');
        this.form = this.querySelector('form[action*="/cart/add"]');
        this.addButton = this.querySelector('[data-add-to-cart-trigger]');
        this.addButtonText = this.addButton ? this.addButton.querySelector('.text') : null;

        this.onTypeChange = this.onTypeChange.bind(this);
        this.onMinus = this.onMinus.bind(this);
        this.onPlus = this.onPlus.bind(this);
        this.onQuantityInput = this.onQuantityInput.bind(this);
        this.onSwitchToSample = this.onSwitchToSample.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.render = this.render.bind(this);

        this.proxyInputs = Array.from(this.querySelectorAll('[data-sample-toggle-input]'));
        this.onProxyChange = this.onProxyChange.bind(this);

        this.typeInputs().forEach((input) => input.addEventListener('change', this.onTypeChange));
        this.proxyInputs.forEach((input) => input.addEventListener('change', this.onProxyChange));
        if (this.minusButton) this.minusButton.addEventListener('click', this.onMinus);
        if (this.plusButton) this.plusButton.addEventListener('click', this.onPlus);
        if (this.quantityInput) this.quantityInput.addEventListener('input', this.onQuantityInput);
        if (this.form) this.form.addEventListener('submit', this.onSubmit);
        this.addEventListener('click', this.onSwitchToSample);

        // The theme updates the variant (and rewrites the Add to Cart label) after option
        // changes. Re-render after each variant change, and re-apply the label if the theme
        // writes over it.
        if (this.variantSelection) {
          this.variantObserver = new MutationObserver(() => requestAnimationFrame(this.render));
          this.variantObserver.observe(this.variantSelection, {
            attributes: true,
            attributeFilter: ['variant'],
          });
        }
        if (this.addButtonText) {
          this.labelObserver = new MutationObserver(() => this.applyButtonLabel());
          this.labelObserver.observe(this.addButtonText, { childList: true, characterData: true, subtree: true });
        }

        this.render();
      }

      disconnectedCallback() {
        this.typeInputs().forEach((input) => input.removeEventListener('change', this.onTypeChange));
        this.proxyInputs.forEach((input) => input.removeEventListener('change', this.onProxyChange));
        if (this.minusButton) this.minusButton.removeEventListener('click', this.onMinus);
        if (this.plusButton) this.plusButton.removeEventListener('click', this.onPlus);
        if (this.quantityInput) this.quantityInput.removeEventListener('input', this.onQuantityInput);
        if (this.form) this.form.removeEventListener('submit', this.onSubmit);
        this.removeEventListener('click', this.onSwitchToSample);
        if (this.variantObserver) this.variantObserver.disconnect();
        if (this.labelObserver) this.labelObserver.disconnect();
      }

      readPrices() {
        const el = this.querySelector('[data-variant-prices]');
        try {
          return el ? JSON.parse(el.textContent) : {};
        } catch (e) {
          return {};
        }
      }

      typeInputs() {
        return Array.from(this.querySelectorAll(`input[name="${this.typeInputName}"]`));
      }

      // Mode comes from the checked Type radio's value handle (wallpaper-sample vs anything else),
      // not exact text, so "Square feet" / "Square Feet" spellings both work.
      currentMode() {
        const checked = this.typeInputs().find((input) => input.checked);
        if (!checked) return this.dataset.mode;
        return checked.classList.contains('swatch--wallpaper-sample') ? 'sample' : 'wallpaper';
      }

      quantity() {
        const value = this.quantityInput ? parseInt(this.quantityInput.value, 10) : 1;
        return isNaN(value) || value < 1 ? 1 : value;
      }

      setQuantity(value) {
        if (!this.quantityInput) return;
        this.quantityInput.value = Math.max(1, value);
        if (this.minusButton) this.minusButton.toggleAttribute('disabled', this.quantity() <= 1);
      }

      // Proxy value is the Type value handle; the hidden radios carry it as a swatch--<handle> class.
      selectType(handle) {
        const input = this.typeInputs().find((el) => el.classList.contains(`swatch--${handle}`));
        if (!input || input.checked) return;
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      onProxyChange(event) {
        this.selectType(event.target.value);
      }

      onTypeChange() {
        if (this.currentMode() === 'sample') this.setQuantity(1);
        this.render();
      }

      // stopPropagation keeps the theme's body-level quantity handler from also stepping
      // the value (the old inline buy-box script did the same).
      onMinus(event) {
        event.stopPropagation();
        if (this.quantity() > 1) this.setQuantity(this.quantity() - 1);
        this.render();
      }

      onPlus(event) {
        event.stopPropagation();
        this.setQuantity(this.quantity() + 1);
        this.render();
      }

      onQuantityInput() {
        if (this.quantityInput.value !== '' && this.quantity() !== parseInt(this.quantityInput.value, 10)) {
          this.setQuantity(1);
        }
        this.render();
      }

      onSwitchToSample(event) {
        if (!event.target.closest('[data-switch-to-sample]')) return;
        this.selectType('wallpaper-sample');
        const sampleProxy = this.proxyInputs.find((input) => input.value === 'wallpaper-sample');
        if (sampleProxy) sampleProxy.focus();
      }

      onSubmit() {
        if (this.currentMode() === 'sample') this.setQuantity(1);
      }

      render() {
        const mode = this.currentMode();
        this.dataset.mode = mode;
        this.proxyInputs.forEach((input) => {
          input.checked = (input.value === 'wallpaper-sample') === (mode === 'sample');
        });

        if (this.totalEl) {
          const variantId = this.variantSelection ? this.variantSelection.getAttribute('variant') : null;
          const cents = this.prices[variantId];
          if (typeof cents === 'number') {
            this.totalEl.textContent = this.formatMoney(cents * this.quantity());
          }
        }

        if (this.warningEl) {
          this.warningEl.hidden = !(mode === 'wallpaper' && this.quantity() < this.threshold);
        }

        this.applyButtonLabel();
      }

      // Leave the theme's "Sold out" / "Unavailable" states alone: only relabel an enabled button.
      applyButtonLabel() {
        if (!this.addButton || !this.addButtonText || this.addButton.disabled) return;
        const label =
          this.currentMode() === 'sample'
            ? this.sampleLabel
            : this.wallpaperLabel.replace('[qty]', this.quantity());
        if (!label) return;
        this.addButton.setAttribute('data-label', label);
        if (this.addButtonText.textContent !== label) this.addButtonText.textContent = label;
      }

      // Uses the shop's money format ("${{amount}}" on this store); covers the common
      // {{amount}} / {{amount_no_decimals}} placeholders.
      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '${{amount}}';
        const amount = (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const noDecimals = Math.round(cents / 100).toLocaleString('en-US');
        return format
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/, noDecimals)
          .replace(/\{\{\s*amount\s*\}\}/, amount);
      }
    },
  );
}
